import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { MatDrawerToggleResult } from '@angular/material/sidenav';
import { noop, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import assign from 'lodash-es/assign';
import moment from 'moment';
import { Task, TaskStatus } from '../../../shared/modules/legacy-tasks/tasks.types';
import { AggregatedTasksService } from 'app/modules/aggregated-tasks/aggregated-tasks.service';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { User } from 'app/core/models';
import { AccountService } from 'app/core/services';
import { Project } from 'app/modules/projects/projects.types';
import { FileResource } from 'app/shared/modules/folders/folders.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Account } from 'app/core/models/account.types';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { HttpClient } from '@angular/common/http';
import { ENTER } from '@angular/cdk/keycodes';
import {
  MatLegacyAutocomplete as MatAutocomplete,
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
} from '@angular/material/legacy-autocomplete';
import { ContactsService } from 'app/modules/contacts/contacts.service';
import { ContactsDetailsComponent } from '../details/details.component';
import { Contact } from '../contacts.types';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { FeatureFlag, NewPermission } from '@core/enums';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { LayoutService } from 'app/layout/layout.service';

@Component({
  selector: 'contact-task-details',
  templateUrl: './contact-task-details.component.html',
  styleUrls: ['./contact-task-details.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactTaskDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  task: Task;
  taskForm: UntypedFormGroup;
  tasks: Task[];
  users: User[];
  contact: Contact;
  project: Project;
  account: Account;
  public results: any[] | null;
  selectable = true;
  removable = true;
  contactList: any[] = [];
  selectedUsers: any[] = [];
  userCtrl: UntypedFormControl;
  separatorKeysCodes: number[] = [ENTER];

  darkTheme: NgxMaterialTimepickerTheme = {
    container: {
      bodyBackgroundColor: '#424242',
      buttonColor: '#fff',
    },
    dial: {
      dialBackgroundColor: '#555',
    },
    clockFace: {
      clockFaceBackgroundColor: '#555',
      clockHandColor: '#9fbd90',
      clockFaceTimeInactiveColor: '#fff',
    },
  };

  NewPermission = NewPermission;
  canEdit = true;

  public todoSaveBtnDisabled: boolean;
  public workflowStates: TaskStatus[];

  // Private
  private _dueDatePanelOverlayRef: OverlayRef;
  private _dueDatePanelTemplatePortal: TemplatePortal;
  private _priorityPanelOverlayRef: OverlayRef;
  private _priorityPanelTemplatePortal: TemplatePortal;
  private _unsubscribeAll: Subject<void>;

  @ViewChild('dueDatePanelOrigin')
  private _dueDatePanelOrigin: MatButton;

  @ViewChild('dueDatePanel')
  private _dueDatePanel: TemplateRef<any>;

  @ViewChild('priorityPanelOrigin')
  private _priorityPanelOrigin: MatButton;

  @ViewChild('priorityPanel')
  private _priorityPanel: TemplateRef<any>;

  @ViewChild('titleField')
  private _titleField: ElementRef;

  @ViewChild('userInput')
  userInput: ElementRef<HTMLInputElement>;

  @ViewChild('auto')
  matAutocomplete: MatAutocomplete;

  /**
   * Constructor
   *
   * @param {ActivatedRoute} _activatedRoute
   * @param {ChangeDetectorRef} _changeDetectorRef
   * @param {FormBuilder} _formBuilder
   * @param {Renderer2} _renderer2
   * @param {Router} _router
   * @param {TasksListComponent} _tasksListComponent
   * @param {AggregatedTasksService} _aggregatedTasksService
   * @param {Overlay} _overlay
   * @param {ViewContainerRef} _viewContainerRef
   */
  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _formBuilder: UntypedFormBuilder,
    private _router: Router,
    private _httpClient: HttpClient,
    private _aggregatedTasksService: AggregatedTasksService,
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef,
    private _accountService: AccountService,
    private _foldersTabService: FoldersTabService,
    private _projectsService: ProjectsService,
    private _contactsService: ContactsService,
    private confirmService: ConfirmService,
    private readonly store: Store,
    private readonly layoutService: LayoutService,
  ) {
    this.userCtrl = new UntypedFormControl();
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Open the drawer
    // this._tasksListComponent.matDrawer.open();
    this._accountService.currentAccount.subscribe((accountData) => {
      this._contactsService.accountId = accountData.id;
    });

    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      this.canEdit = this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_EDIT));
    }

    // Create the task form
    this.taskForm = this._formBuilder.group(
      {
        id: [''],
        type: [''],
        title: [''],
        assigned_user_id: [''],
        description: [''],
        is_complete: [false],
        due_date: [null],
        priority: [0],
        order: [0],
        workflow_state: [''],
        project_title: [''],
        project_id: [''],
      },
      {
        updateOn: 'blur',
      },
    );

    if (!this.canEdit) {
      Object.keys(this.taskForm.controls).forEach((control) => this.taskForm.controls[control].disable());
      this.userCtrl.disable();
    }

    this._accountService.currentAccount.subscribe((accountData) => {
      this.account = accountData;
      this._foldersTabService.accountId = accountData.id;
    });

    this._accountService.users$.pipe(takeUntil(this._unsubscribeAll)).subscribe((users: User[]) => {
      this.users = users;
      this._changeDetectorRef.markForCheck();
    });

    this._projectsService.project$.subscribe((project: Project) => {
      this.project = project;
    });

    // Get the task
    this._aggregatedTasksService.task$.pipe(takeUntil(this._unsubscribeAll)).subscribe((task: Task) => {
      // Get the task
      this.task = task || {};
      this.selectedUsers = this.task.contacts || [];
      // Patch values to the form from the task
      this.taskForm.patchValue(task, { emitEvent: false });

      // Mark for check
      this._changeDetectorRef.markForCheck();
    });

    this._aggregatedTasksService.tasks$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((tasks) => (this.tasks = tasks));

    this.taskForm
      .get('project_title')
      .valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(400), distinctUntilChanged())
      .subscribe((value) => {
        if (!value.project_id) {
          this.taskForm.get('project_id').setValue('');
          let query = `{
                        projects(filter:{q: "${value}"}, first: 10) {
                        id
                        title
                        pipeline_id
                        address_string
                        featured_image_url
                        }
                    }`;
          this._httpClient.post(`graphql`, { query: query }).subscribe((response: any) => {
            let projects = response.data.projects;
            this.results = projects;
            this._changeDetectorRef.markForCheck();
          });
        }
      });
    this._contactsService.contact$.subscribe((contact: Contact) => {
      this.contact = contact;
      if (!this.selectedUsers.find((user) => user.id == this.contact.id)) {
        this.selectedUsers.push({
          id: this.contact.id,
          name: this.contact.name,
        });
      }
    });
    // Update task when there is a value change on the task form
    this.taskForm.valueChanges
      .pipe(
        tap((value) => {
          // Update the task object
          this.task = assign(this.task, value);
        }),
        debounceTime(300),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe((value) => {
        // Update the task on the server
        const task = { ...value, contact_ids: this.selectedUsers.map((user) => user.id) };
        this._aggregatedTasksService.updateTask(task, this.task).subscribe();

        // Mark for check
        this._changeDetectorRef.markForCheck();
      });

    // Listen for NavigationEnd event to focus on the title field
    this._router.events
      .pipe(
        takeUntil(this._unsubscribeAll),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        // Focus on the title field
        this._titleField.nativeElement.focus();
      });

    this._aggregatedTasksService.getTaskStatuses().subscribe((taskStatuses) => {
      this.workflowStates = taskStatuses;
    });
    this.userCtrl.valueChanges
      .pipe(filter(Boolean), takeUntil(this._unsubscribeAll), debounceTime(400), distinctUntilChanged())
      .subscribe((query: string) => {
        this._contactsService.getContacts(1, 20, 'first_name', 'asc', query).subscribe((response: any) => {
          this.contactList = response.contacts;
          this._changeDetectorRef.markForCheck();
        });
      });
  }

  /**
   * After view init
   */
  ngAfterViewInit(): void {
    // // Listen for matDrawer opened change
    // this._tasksListComponent.matDrawer.openedChange
    //   .pipe(
    //     takeUntil(this._unsubscribeAll),
    //     filter((opened) => opened),
    //   )
    //   .subscribe(() => {
    //     // Focus on the title element
    //     this._titleField.nativeElement.focus();
    //   });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions

    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();

    // Dispose the overlays if they are still on the DOM
    if (this._dueDatePanelOverlayRef) {
      this._dueDatePanelOverlayRef.dispose();
    }

    if (this._priorityPanelOverlayRef) {
      this._priorityPanelOverlayRef.dispose();
    }
  }

  onCheckListUpdated(updatedTask: Task): void {
    this._aggregatedTasksService
      .updateTask(updatedTask, this.task)
      .pipe(distinctUntilChanged(), takeUntil(this._unsubscribeAll))
      .subscribe(noop);
  }

  getTaskStatus(status): TaskStatus {
    if (!status) {
      return;
    }

    return this.workflowStates.find((taskStatus) => taskStatus.key === status);
  }

  loadTask(): void {
    this._aggregatedTasksService.getTaskById(this.task.task_list_id, this.task.id).subscribe();
  }

  autocompleteSelected($event): void {
    this.taskForm.get('project_id').patchValue($event.option.value?.id, { emitEvent: false });
    this.taskForm.get('project_title').patchValue($event.option.value?.title, { emitEvent: false });
    this.task = assign(this.task, this.taskForm.value);
    this._aggregatedTasksService.updateTask(this.taskForm.value, this.task).subscribe();
  }

  onFileChanged($event) {
    let fileToUpload = $event.target.files[0];
    let fileResource: FileResource = {};
    fileResource.folder_resource_id = this.project.root_folder_resource_id;
    fileResource.name = fileToUpload.name;
    fileResource.related_to_type = 'Task';
    fileResource.related_to_id = this.task.id;
    this._foldersTabService.moduleId = this.project.id;
    this._foldersTabService.moduleBaseUrl = 'projects';

    this._foldersTabService.createFile(fileResource.folder_resource_id, fileResource).subscribe((fileResource) => {
      this._foldersTabService
        .uploadDocument(fileResource.folder_resource_id, fileResource.id, fileToUpload)
        .subscribe((document) => {
          this.loadTask();
        });
    });
  }

  downloadDocument(fileResource): void {
    if (fileResource.documents.length > 0) {
      let document = fileResource.documents[0];
      window.open(document.original_url, '_blank');
    }
  }

  deleteFile(fileResource): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this._foldersTabService.deleteFile(fileResource.folder_resource_id, fileResource.id).subscribe((response) => {
          this.loadTask();
        });
      });
  }

  /**
   * Close the drawer
   */
  closeDrawer(): void {
    // let route = this._activatedRoute;
    // Navigate to the next task if available
    // this._router.navigate(['../../'], { relativeTo: route });
    //  this._tasksListComponent.matDrawer.close();

    this.layoutService.emitCommon('crm-tasks', false);
  }

  /**
   * Toggle the completed status
   */
  toggleCompleted(): void {
    // Get the form control for 'completed'
    const completedFormControl = this.taskForm.get('is_complete');

    // Toggle the completed status
    completedFormControl.setValue(!completedFormControl.value);
    if (completedFormControl.value) {
      this.taskForm.get('workflow_state').setValue('complete');
    } else {
      this.taskForm.get('workflow_state').setValue('in_progress');
    }
  }

  /**
   * Open priority panel
   */
  openPriorityPanel(): void {
    // Create the overlay
    this._priorityPanelOverlayRef = this._overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._priorityPanelOrigin._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    // Create a portal from the template
    this._priorityPanelTemplatePortal = new TemplatePortal(this._priorityPanel, this._viewContainerRef);

    // Attach the portal to the overlay
    this._priorityPanelOverlayRef.attach(this._priorityPanelTemplatePortal);

    // Subscribe to the backdrop click
    this._priorityPanelOverlayRef.backdropClick().subscribe(() => {
      // Close the panel
      this.closePriorityPanel();
    });
  }

  /**
   * Close the priority panel if it's open
   */
  closePriorityPanel(): void {
    // If overlay exists and attached...
    if (this._priorityPanelOverlayRef && this._priorityPanelOverlayRef.hasAttached()) {
      // Detach it
      this._priorityPanelOverlayRef.detach();
    }

    // If template portal exists and attached...
    if (this._priorityPanelTemplatePortal && this._priorityPanelTemplatePortal.isAttached) {
      // Detach it
      this._priorityPanelTemplatePortal.detach();
    }

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Open due date panel
   */
  openDueDatePanel(): void {
    // Create the overlay
    this._dueDatePanelOverlayRef = this._overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._dueDatePanelOrigin._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    // Create a portal from the template
    this._dueDatePanelTemplatePortal = new TemplatePortal(this._dueDatePanel, this._viewContainerRef);

    // Attach the portal to the overlay
    this._dueDatePanelOverlayRef.attach(this._dueDatePanelTemplatePortal);

    // Subscribe to the backdrop click
    this._dueDatePanelOverlayRef.backdropClick().subscribe(() => {
      // Close the panel
      this.closeDueDatePanel();
    });
  }

  /**
   * Close the due date panel
   */
  closeDueDatePanel(): void {
    // If overlay exists and attached...
    if (this._dueDatePanelOverlayRef && this._dueDatePanelOverlayRef.hasAttached()) {
      // Detach it
      this._dueDatePanelOverlayRef.detach();
    }

    // If template portal exists and attached...
    if (this._dueDatePanelTemplatePortal && this._dueDatePanelTemplatePortal.isAttached) {
      // Detach it
      this._dueDatePanelTemplatePortal.detach();
    }

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Update the due date
   *
   * @param event
   */
  updateDueDate(event: moment.Moment): void {
    // Get the form control for 'dueDate'
    const dueDateFormControl = this.taskForm.get('due_date');

    // Update it
    dueDateFormControl.setValue(event.toISOString());

    // Close the due date panel...
    this.closeDueDatePanel();
  }

  /**
   * Remove the due date
   */
  removeDueDate(): void {
    // Get the form control for 'dueDate'
    const dueDateFormControl = this.taskForm.get('due_date');

    // Clear the due date
    dueDateFormControl.setValue(null);

    // Close the due date panel...
    this.closeDueDatePanel();
  }

  /**
   * Check if the task is overdue or not
   */
  isOverdue(): boolean {
    return moment(this.task.due_date, moment.ISO_8601).isBefore(moment(), 'days');
  }

  /**
   * Delete the task
   */
  deleteTask(): void {
    // Delete the task
    this._aggregatedTasksService.deleteTask(this.task).subscribe((taskDeleted) => {
      // Return if the task wasn't deleted...
      if (!taskDeleted) {
        return;
      }

      const index = this.tasks.findIndex((item) => item.id === taskDeleted.id);
      this.tasks.splice(index, 1);

      this.closeDrawer();
      this._changeDetectorRef.markForCheck();
    });

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  taskTimeChanged(time: EventEmitter<string>) {
    const makeTime = moment(`${time}`, 'LT');
    const makeDate = moment(this.task.due_date);
    makeDate.set({
      hour: +makeTime.format('HH'),
      minute: +makeTime.format('mm'),
    });
    this.task.due_date = makeDate.format();
    const dueDateFormControl = this.taskForm.get('due_date');

    // Update it
    dueDateFormControl.setValue(makeDate.format());
  }

  removeUser(user: any): void {
    if (user.id !== this.contact.id) {
      this.selectedUsers.splice(
        this.selectedUsers.findIndex((item) => item.id === user.id),
        1,
      );
      const task = { ...this.taskForm.value, contact_ids: this.selectedUsers.map((user) => user.id) };
      this._aggregatedTasksService.updateTask(task, this.task).subscribe();
      this._changeDetectorRef.markForCheck();
    }
  }

  selectedUser(event: MatAutocompleteSelectedEvent): void {
    this.selectedUsers.push({
      id: event.option.id,
      name: event.option.viewValue,
    });
    const task = { ...this.taskForm.value, contact_ids: this.selectedUsers.map((user) => user.id) };
    this._aggregatedTasksService.updateTask(task, this.task).subscribe();
    this.userInput.nativeElement.value = '';
    this.userCtrl.setValue(null);
  }

  calendarFilter(calendarDate: moment.Moment): boolean {
    return calendarDate.startOf('day') >= moment().startOf('day');
  }
}
